import { ComponentProps, useEffect, useMemo } from 'react';
import Hero from 'components/Hero/Hero';
import useHeroTheme from 'components/Hero/useHeroTheme';
import Layout from 'components/Layout/Layout';
import ModuleItem from 'components/Modules';
import Redirect from 'components/Redirect';
import ReferralPopup from 'components/ReferralPopup/ReferralPopup';
import useMarketingParams from 'hooks/useMarketingParams';
import { fetchBrandConfig } from 'services/config/ssr';
import { fetchAllPagesSlugs, fetchPage } from 'services/page/ssr';
import useReferralPopup from 'services/referrals';
import mergeStaticProps, { StaticProps } from 'utils/mergeStaticProps';

import BlogPostPage from './blog/[slug]';
import BlogPage from './blog';

type PageProps = StaticProps<typeof fetchPage>;
type BlogPageProps = ComponentProps<typeof BlogPage>;
type BlogPostPageProps = ComponentProps<typeof BlogPostPage>;
type CatchAllPageProps = PageProps | BlogPageProps | BlogPostPageProps;

export default function Page(props: CatchAllPageProps) {
  if ('renderAs' in props && props.renderAs === 'blog-index') {
    return <BlogPage {...props} />;
  }

  if ('renderAs' in props && props.renderAs === 'blog-post') {
    return <BlogPostPage {...props} />;
  }

  return <GenericPage {...props} />;
}

function GenericPage({ page }: PageProps) {
  const popup = useReferralPopup();
  const theme = useHeroTheme(page.hero || {});
  const { asValues: utms, isReady, updateUtms } = useMarketingParams();

  const { isLoading, is404 } = useMemo(() => {
    const requiredUtms = page.showOnlyForUtms;
    if (!requiredUtms || !Object.keys(requiredUtms).length) {
      return { isLoading: false, is404: false };
    }

    if (!isReady) return { isLoading: true, is404: false };
    const hasIvalidUtm = Object.entries(requiredUtms).some(([key, value]) => utms[key] !== value);

    return { isLoading: false, is404: hasIvalidUtm };
  }, [isReady, utms, page.showOnlyForUtms]);

  useEffect(() => {
    if (isLoading || !page.pageEmbeddedUtms || !Object.keys(page.pageEmbeddedUtms).length) return;

    updateUtms(page.pageEmbeddedUtms);
  }, [isLoading, page.pageEmbeddedUtms, updateUtms]);

  if (isLoading) return null;

  if (is404) return <Redirect url="/404" />;

  return (
    <Layout theme={theme}>
      {page.hero && <Hero {...page.hero} />}
      {popup && <ReferralPopup {...popup} />}
      {page.pageSections?.length ? page.pageSections.map(ModuleItem) : null}
    </Layout>
  );
}

export const getStaticProps = mergeStaticProps(fetchBrandConfig, fetchPage);

export const getStaticPaths = fetchAllPagesSlugs;
